<template>
  <v-dialog v-model="internalShow" persistent max-width="420">
    <v-card>
      <v-toolbar dense flat color="primary" dark>
        <v-toolbar-title>
          Enroll Fingerprints —
          {{ employee && employee.name ? employee.name : "Unknown" }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          icon
          v-if="capturedCount < 3"
          :disabled="isProcessing"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="text-center">
        <!-- 🟢 Clear instruction for the user -->
        <p class="mb-3 font-weight-medium" style="font-size: 15px">
          Place your finger firmly on the sensor and
          <strong>keep it there until the light turns on and off</strong>.
          You’ll repeat this three times.
        </p>

        <div class="d-flex justify-space-around mt-4">
          <div
            v-for="i in 3"
            :key="i"
            class="finger-box d-flex align-center justify-center"
            :class="{ captured: capturedCount >= i }"
          >
            <v-img
              v-if="capturedImages[i - 1]"
              :src="capturedImages[i - 1]"
              contain
              width="60"
              height="60"
            />
            <v-icon v-else color="grey lighten-1" size="36">
              mdi-fingerprint
            </v-icon>

            <v-icon
              v-if="capturedCount >= i"
              color="success"
              size="22"
              class="check-icon"
            >
              mdi-check
            </v-icon>
          </div>
        </div>

        <!-- 🟡 Progress text -->
        <div v-if="capturedCount < 3" class="mt-4">
          <v-progress-linear
            color="primary"
            height="6"
            :value="(capturedCount / 3) * 100"
          />
          <p class="mt-2">{{ statusText }}</p>
        </div>

        <!-- 🌀 Show spinner during fingerprint processing -->
        <div v-else-if="isProcessing" class="mt-4 text-center">
          <v-progress-circular
            indeterminate
            color="primary"
            size="48"
            class="mb-2"
          ></v-progress-circular>
          <p class="font-weight-medium">{{ statusText }}</p>
        </div>

        <!-- ✅ Enrollment Complete Section -->
        <div v-else class="mt-4">
          <v-icon color="success" size="40">mdi-check-circle</v-icon>
          <p class="font-weight-medium mt-2">Enrollment Complete!</p>

          <!-- Auto close countdown -->
          <v-progress-linear
            v-if="autoCloseProgress > 0"
            :value="autoCloseProgress"
            height="6"
            color="success"
            class="mt-3"
          ></v-progress-linear>
          <p
            v-if="autoCloseProgress > 0"
            class="caption mt-1 grey--text text--darken-1"
          >
            Closing in {{ Math.ceil((5000 - autoCloseTimeElapsed) / 1000) }}s…
          </p>
        </div>
      </v-card-text>

      <v-card-actions class="justify-center">
        <v-btn color="primary" @click="close" v-if="capturedCount < 3">
          Close
        </v-btn>
        <v-btn
          color="success"
          @click="close"
          v-else
          :disabled="autoCloseProgress < 100"
        >
          Confirm
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "FingerprintEnrollmentDialog",
  props: {
    value: { type: Boolean, required: true },
    employee: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      isProcessing: false,
      internalShow: this.value,
      capturedCount: 0,
      capturedImages: ["", "", ""],
      statusText: "Waiting for fingerprint...",
      autoCloseProgress: 0,
      autoCloseTimer: null,
      autoCloseTimeElapsed: 0,
    };
  },
  watch: {
    value(val) {
      this.internalShow = val;
    },
    internalShow(val) {
      this.$emit("input", val);
    },
  },
  mounted() {
    // 🟢 Enrollment progress callback from Android
    window.onEnrollmentProgress = (scanNumber) => {
      this.capturedCount = scanNumber;

      if (scanNumber < 3) {
        this.statusText = `Captured ${scanNumber}/3, please scan again.`;
      } else if (scanNumber === 3) {
        // ✅ Immediately show "processing" feedback
        this.statusText =
          "All fingerprints captured. Processing, please wait...";
        this.isProcessing = true;

        // Disable the top Close button during processing
        const closeBtn = document.querySelector(".v-toolbar .v-btn");
        if (closeBtn) closeBtn.disabled = true;
      }
    };

    window.onDuplicateFingerprint = (name) => {
      // Show SweetAlert warning
      this.$swal
        .fire({
          title: "Duplicate Finger Found",
          text: `This fingerprint already belongs to ${name}. Please use another finger.`,
          icon: "warning",
          confirmButtonColor: "#d33",
          confirmButtonText: "OK",
          position: "center",
        })
        .then(() => {
          // After user closes the alert, reset and close the dialog
          this.resetDialog();
          this.internalShow = false;
          this.$emit("input", false);
          this.$emit("close");
        });
    };

    // 🟢 Final enrollment callback from Android (after backend merge)
    window.onFingerprintEnrollment = (csvData) => {
      this.statusText = "Processing fingerprint data… please wait";
      this.capturedCount = 3;

      setTimeout(() => {
        const templates = csvData.split(",");
        this.isProcessing = false; // stop showing spinner
        this.statusText = "Enrollment complete!";
        this.$emit("enrolled", templates);
        this.startAutoCloseCountdown();

        // Re-enable top Close button
        const closeBtn = document.querySelector(".v-toolbar .v-btn");
        if (closeBtn) closeBtn.disabled = false;
      }, 800);
    };
  },
  methods: {
    resetDialog() {
      if (this.autoCloseTimer) clearInterval(this.autoCloseTimer);
      this.capturedCount = 0;
      this.capturedImages = ["", "", ""];
      this.statusText = "Waiting for fingerprint...";
      this.autoCloseProgress = 0;
      this.autoCloseTimeElapsed = 0;
      this.isProcessing = false;
    },
    startAutoCloseCountdown() {
      const total = 5000; // 5 seconds
      const step = 100; // update every 0.1 sec
      this.autoCloseProgress = 0;
      this.autoCloseTimeElapsed = 0;

      this.autoCloseTimer = setInterval(() => {
        this.autoCloseTimeElapsed += step;
        this.autoCloseProgress = Math.min(
          (this.autoCloseTimeElapsed / total) * 100,
          100
        );
        if (this.autoCloseTimeElapsed >= total) {
          clearInterval(this.autoCloseTimer);
          this.autoCloseTimer = null;
          this.close();
        }
      }, step);
    },
    close() {
      if (this.autoCloseTimer) clearInterval(this.autoCloseTimer);
      this.capturedCount = 0;
      this.capturedImages = ["", "", ""];
      this.statusText = "Waiting for fingerprint...";
      this.autoCloseProgress = 0;
      this.autoCloseTimeElapsed = 0;
      this.internalShow = false;
      this.$emit("input", false);
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.finger-box {
  width: 80px;
  height: 80px;
  border: 2px dashed #ccc;
  border-radius: 10px;
  position: relative;
}
.finger-box.captured {
  border-color: #4caf50;
  background-color: #e8f5e9;
}
.check-icon {
  position: absolute;
  top: 4px;
  right: 4px;
}
</style>